import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@cardboard-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageURLInput as ImageURLInput } from './ImageURLInput';
import { t } from '@lingui/macro';
import { CoverImageInput } from 'apps/TenantApp/screens/Event/components/EventHeader/__generated__/EventHeader_updateEventImageUrl_Mutation.graphql';
import { CroppableImage, CropVariant } from './CroppableImage';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import { DefaultImagesGallery } from './DefaultImagesGallery';
import { useSimpleFileUploader } from 'utils/upload/useSimpleFileUploader';

export interface ImageUploadModalProps {
  isOpen: boolean;
  cropVariant?: CropVariant;
  imageURL: string;

  onClose(): void;

  onImageSelected({
    newImage,
    newLocalImageURL,
    onComplete,
  }: {
    newImage: CoverImageInput | null;
    newLocalImageURL: string;
    onComplete: () => void;
  }): void;

  headingText: string;
  showDefaultImagesGallery?: boolean;
  defaultImage?: string;
}

export const ImageUploadModal = ({
  isOpen,
  cropVariant,
  onClose,
  onImageSelected,
  headingText,
  imageURL,
  showDefaultImagesGallery,
  defaultImage,
}: ImageUploadModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sourceUrl, setSourceUrl] = useState<string | undefined>();
  const [localFile, setLocalFile] = useState<File | undefined>();
  const [croppedFileURL, setCroppedFileURL] = useState<string | undefined>();
  const [localFileURL, setLocalFileURL] = useState<string>('');
  const [imageName, setImageName] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const {
    isOpen: isConfirmationOpen,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();

  const { upload } = useSimpleFileUploader('AVATARS');

  const uploadFile = useCallback(
    async (file: File) => {
      const uppyFile = await upload(file);
      onImageSelected({
        newImage: {
          kind: 'UPLOAD',
          value: uppyFile.meta.uploadToken as string,
        },
        newLocalImageURL: croppedFileURL || sourceUrl || localFileURL || '',
        onComplete: () => {
          setIsLoading(false);
          onClose();
        },
      });
    },
    [upload, onImageSelected, croppedFileURL, sourceUrl, localFileURL],
  );

  const clearSelectedFile = () => {
    setLocalFile(undefined);
    setLocalFileURL('');
    setCroppedFileURL(undefined);
    setSourceUrl(undefined);
    setIsLoading(false);
    if (!!fileInputRef.current?.value) {
      fileInputRef.current.value = '';
    }
  };

  const deleteImage = () => {
    onImageSelected({
      newImage: { kind: 'URL', value: '' },
      newLocalImageURL: '',
      onComplete: () => {
        onCloseConfirmation();
        onClose();
      },
    });
  };

  const openLocalFileSelector = useCallback(() => {
    fileInputRef?.current?.click();
  }, [fileInputRef?.current]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      clearSelectedFile();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!!localFile) {
      const localFileURL = URL.createObjectURL(localFile);
      setSourceUrl(localFileURL);
      setImageName(localFile.name);
      setCroppedFileURL(localFileURL);
    } else {
      setSourceUrl('');
    }
  }, [localFile]);

  const onConfirmFile = (file: File) => {
    setIsLoading(true);
    setCroppedFileURL(URL.createObjectURL(file));
    uploadFile(file);
  };

  return (
    <>
      <Input
        hidden
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={({ target }) => {
          const files = Array.from(target?.files || []);
          const file = files[0];
          setLocalFile(file);
        }}
      />

      <ConfirmationDialog
        action={{
          name: t`Remove image?`,
          onAction: deleteImage,
        }}
        isOpen={isConfirmationOpen}
        onClose={onCloseConfirmation}
      >
        {t`This will remove your current image.`}{' '}
        {t`You can upload a new image at a later time.`}
      </ConfirmationDialog>

      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={!sourceUrl}>
        <ModalOverlay />
        <ModalContent data-qa="image-upload">
          <ModalHeader>{headingText}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs isLazy>
              <TabList>
                <Tab>{t`Upload`}</Tab>
                <Tab>{t`Via link`}</Tab>
                {showDefaultImagesGallery ? <Tab>{t`Gallery`}</Tab> : null}
                <Spacer />
                {imageURL && (
                  <Button
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    alignSelf="center"
                    onClick={onOpenConfirmation}
                    sx={{
                      opacity: 0.5,
                      _hover: {
                        opacity: 1,
                      },
                    }}
                  >
                    {t`Remove`}
                  </Button>
                )}
              </TabList>
              <TabPanels>
                <TabPanel py={4} px={0}>
                  {sourceUrl ? (
                    <CropAndConfirm
                      sourceUrl={sourceUrl}
                      imageName={imageName || 'UploadedFile'}
                      cropVariant={cropVariant || 'none'}
                      isUploading={isLoading}
                      onConfirmFile={onConfirmFile}
                      onCancel={clearSelectedFile}
                    />
                  ) : (
                    <Button
                      onClick={openLocalFileSelector}
                    >{t`Select File`}</Button>
                  )}
                </TabPanel>
                <TabPanel py={4} px={0}>
                  {sourceUrl ? (
                    <CropAndConfirm
                      sourceUrl={sourceUrl}
                      imageName={imageName || 'UploadedFile'}
                      cropVariant={cropVariant || 'none'}
                      isUploading={isLoading}
                      onConfirmFile={onConfirmFile}
                      onCancel={clearSelectedFile}
                    />
                  ) : (
                    <ImageURLInput onNewSourceUrl={setSourceUrl} />
                  )}
                </TabPanel>
                {showDefaultImagesGallery ? (
                  <TabPanel py={4} px={0}>
                    <DefaultImagesGallery
                      onPickImage={(imageSrc) => {
                        onImageSelected({
                          newImage: { kind: 'URL', value: imageSrc },
                          newLocalImageURL: imageSrc,
                          onComplete: onClose,
                        });
                      }}
                      defaultImage={defaultImage}
                    />
                  </TabPanel>
                ) : null}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ImageUploadModal;

interface CropAndConfirmProps {
  sourceUrl: string;
  imageName: string;
  cropVariant: CropVariant;
  onConfirmFile: (file: File) => void;
  isUploading: boolean;
  onCancel: () => void;
}

const CropAndConfirm = ({
  sourceUrl,
  imageName,
  cropVariant,
  onConfirmFile,
  isUploading,
  onCancel,
}: CropAndConfirmProps) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const saveDisabled = !selectedFile || isUploading;

  return (
    <Stack>
      <CroppableImage
        src={sourceUrl}
        imageName={imageName}
        variant={cropVariant}
        onImageUpdate={setSelectedFile}
      />
      <HStack justify="flex-end">
        <Button variant="ghost" onClick={onCancel}>
          {t`Cancel`}
        </Button>
        <Button
          colorScheme="blue"
          onClick={() => selectedFile && onConfirmFile(selectedFile)}
          isDisabled={saveDisabled}
          isLoading={isUploading}
        >{t`Save`}</Button>
      </HStack>
    </Stack>
  );
};
